/**
 * Walls Step Component
 *
 * Supports two modes:
 * - Full Room: Configure all walls with same settings
 * - Individual Walls: Configure each wall separately with full options
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEstimator } from '../../../context/EstimatorContext';
import { ProductColor, PanelOrientation, WallConfig, SlatWallConfig, GarageConfig } from '../../../types';
import { GARAGE_DOOR_PRESETS, DESIGN_PRESETS, ROOM_PRESETS, applyDesignPreset, getRoomPresetById } from '../../../data/presets';
import { WALL_PANEL_IMAGES, SLATWALL_IMAGES, LEGACY_DESIGN_PRESET_IMAGES, LEGACY_UI_ICONS } from '../../../data/productImages';
import { trackWallColorSelected, trackWallOrientationSelected, trackSlatwallToggled, trackWallConfigured, trackWallAdded } from '../../../utils/gtmTracking';
import { Tooltip } from '../../ui/Tooltip';
import { useToast } from '../../../context/ToastContext';
import { Sparkles, Settings, Copy, Plus } from 'lucide-react';
import { Wall3DViewer } from '../../ui/room3d';
import { DimensionInput, CountInput, UnitToggle } from '../../ui/DimensionInput';
import { useUnitMode } from '../../../hooks/useUnitMode';
import './Steps.css';

/** Wall length + height with one shared ft/in <-> decimal toggle (per wall). */
const WallDimensionFields: React.FC<{
  unitKey: string;
  length: number;
  height: number;
  onChange: (field: 'length' | 'height', value: number) => void;
}> = ({ unitKey, length, height, onChange }) => {
  const { t } = useTranslation('wizard');
  const [mode, setMode] = useUnitMode(unitKey);
  const lengthError = length < 1 || length > 200;
  const heightError = height < 1 || height > 20;
  return (
    <div className="dim-group">
      <div className="wall-dimensions-row">
        <div className="field-group">
          <label className="field-label">{t('walls.fieldLength')}</label>
          <DimensionInput
            value={length}
            onChange={v => onChange('length', v)}
            mode={mode}
            min={1}
            max={200}
            hasError={lengthError}
            ariaLabel={t('walls.lengthAria')}
          />
          {lengthError && <span className="field-hint error">{t('walls.lengthRange')}</span>}
        </div>
        <div className="field-group">
          <label className="field-label">{t('walls.fieldHeight')}</label>
          <DimensionInput
            value={height}
            onChange={v => onChange('height', v)}
            mode={mode}
            min={1}
            max={20}
            hasError={heightError}
            ariaLabel={t('walls.heightAria')}
          />
          {heightError && <span className="field-hint error">{t('walls.heightRange')}</span>}
        </div>
      </div>
      <UnitToggle mode={mode} onChange={setMode} />
    </div>
  );
};

/** SlatWall start height (a dimension) + panel count (a count) with one toggle.
 *  The toggle governs only the start-height ft/in field; the panel count stays
 *  a plain integer input. */
const SlatWallFields: React.FC<{
  unitKey: string;
  startHeight: number;
  numPanels: number;
  startMax: number;
  maxPanels: number;
  onStartHeightChange: (value: number) => void;
  onPanelsChange: (value: number) => void;
  panelsFieldId?: string;
}> = ({
  unitKey,
  startHeight,
  numPanels,
  startMax,
  maxPanels,
  onStartHeightChange,
  onPanelsChange,
  panelsFieldId,
}) => {
  const { t } = useTranslation(['wizard', 'common']);
  const [mode, setMode] = useUnitMode(unitKey);
  const startError = startHeight < 0 || startHeight > startMax;
  const panelsError = numPanels < 0 || numPanels > maxPanels;
  return (
    <div className="row g-4 justify-content-center align-items-start">
      <div className="col-auto">
        <div className="d-flex flex-column align-items-center">
          <label className="field-label text-nowrap mb-2">{t('walls.slatWall.startHeight')}</label>
          <DimensionInput
            value={startHeight}
            onChange={onStartHeightChange}
            mode={mode}
            min={0}
            max={startMax}
            hasError={startError}
            ariaLabel={t('walls.slatWall.startHeightAria')}
          />
          {startError && <span className="field-hint error">{t('walls.slatWall.startRange', { max: startMax.toFixed(1) })}</span>}
          {/* toggle belongs ONLY to start height — keep it directly under it */}
          <UnitToggle mode={mode} onChange={setMode} />
        </div>
      </div>
      <div className="col-auto d-none d-sm-flex align-items-center px-0">
        <div style={{ width: '1px', height: '75%', backgroundColor: '#dee2e6' }}></div>
      </div>
      <div className="col-auto">
        <div className="d-flex flex-column align-items-center">
          <label className="field-label text-nowrap mb-2">{t('walls.slatWall.panels')}</label>
          <CountInput
            value={numPanels}
            onChange={onPanelsChange}
            min={0}
            max={maxPanels}
            hasError={panelsError}
            caption={t('walls.slatWall.panelsCaption')}
            fieldId={panelsFieldId}
            ariaLabel={t('walls.slatWall.panelsAria')}
          />
          {panelsError && <span className="field-hint error">{t('walls.slatWall.panelsRange', { max: maxPanels })}</span>}
        </div>
      </div>
    </div>
  );
};

/** Garage door width + height with one shared toggle. */
const GarageDimensionFields: React.FC<{
  unitKey: string;
  width: number;
  height: number;
  wallLength: number;
  wallHeight: number;
  onChange: (field: 'width' | 'height', value: number) => void;
  widthFieldId?: string;
  heightFieldId?: string;
}> = ({ unitKey, width, height, wallLength, wallHeight, onChange, widthFieldId, heightFieldId }) => {
  const { t } = useTranslation('wizard');
  const [mode, setMode] = useUnitMode(unitKey);
  const widthError = width < 4 || width > wallLength;
  const heightError = height < 6 || height > wallHeight;
  return (
    <div className="dim-group">
      <div className="door-custom-inputs justify-content-center">
        <div className="input-with-label">
          <span className="input-label-text">{t('application.garageDoors.width')}</span>
          <DimensionInput
            value={width}
            onChange={v => onChange('width', v)}
            mode={mode}
            min={4}
            max={wallLength}
            hasError={widthError}
            fieldId={widthFieldId}
            ariaLabel={t('application.garageDoors.widthAria')}
          />
          {widthError && <span className="input-constraint text-danger">{t('walls.garage.widthRange', { max: wallLength })}</span>}
        </div>
        <div className="input-with-label">
          <span className="input-label-text">{t('application.garageDoors.height')}</span>
          <DimensionInput
            value={height}
            onChange={v => onChange('height', v)}
            mode={mode}
            min={6}
            max={wallHeight}
            hasError={heightError}
            fieldId={heightFieldId}
            ariaLabel={t('application.garageDoors.heightAria')}
          />
          {heightError && <span className="input-constraint text-danger">{t('walls.garage.heightRange', { max: wallHeight })}</span>}
        </div>
      </div>
      <UnitToggle mode={mode} onChange={setMode} />
    </div>
  );
};

export const WallsStep: React.FC = () => {
  const {
    state,
    updateWallsConfig,
    updateCeilingConfig,
    addIndividualWall,
    copyIndividualWall,
    updateIndividualWall,
    removeIndividualWall,
    nextStep,
  } = useEstimator();
  const { wallsConfig, calculationMode, individualWalls, dimensionsConfig } = state;
  const { showToast } = useToast();
  const { t } = useTranslation(['wizard', 'common']);

  // Track if user is using a design preset (persisted in global state)
  const usePresets = wallsConfig.usePresets !== false;
  const setUsePresets = (value: boolean) => updateWallsConfig({ usePresets: value });

  // Persist preset selection and slatwall toggle in global state to survive navigation
  const selectedPresetId = wallsConfig.selectedPresetId ?? null;
  const setSelectedPresetId = (id: string | null) => updateWallsConfig({ selectedPresetId: id });
  const slatWallEnabled = wallsConfig.slatWallEnabled ?? wallsConfig.slatWallNumPanels > 0;
  const setSlatWallEnabled = (enabled: boolean) => updateWallsConfig({ slatWallEnabled: enabled });

  // Handler to apply a design preset
  const handleApplyDesignPreset = (presetId: string) => {
    const preset = DESIGN_PRESETS.find(p => p.id === presetId);
    if (!preset) return;

    // Get wall height from dimensions - check custom dimensions first, then preset, then default
    const roomPreset = dimensionsConfig.presetId ? getRoomPresetById(dimensionsConfig.presetId) : undefined;
    const wallHeight = dimensionsConfig.customDimensions?.height ||
      roomPreset?.height ||
      10;

    const config = applyDesignPreset(preset, wallHeight);

    // Update wall configuration
    updateWallsConfig({
      color: config.wallColor,
      orientation: config.wallOrientation,
      slatWallStartHeight: config.slatWall.startHeight,
      slatWallNumPanels: config.slatWall.numPanelRows,
      slatWallColor: config.slatWall.color,
      slatWallCoveragePercent: preset.slatWallCoverage as 0 | 25 | 50,
      usePresets: true,
    });

    // Update ceiling configuration
    updateCeilingConfig({
      include: config.includeCeiling,
      color: config.ceilingColor,
    });

    setSelectedPresetId(presetId);
    setSlatWallEnabled(preset.slatWallCoverage > 0);
  };

  // Get step number for tracking (walls step position depends on mode)
  const wallsStepNumber = calculationMode === 'FullRoom' ? 2 : calculationMode === 'IndividualWalls' ? 2 : 2;

  const handleColorChange = (color: ProductColor) => {
    updateWallsConfig({ color, usePresets: false });
    trackWallColorSelected(calculationMode, color, wallsStepNumber);
  };

  const handleOrientationChange = (orientation: PanelOrientation) => {
    updateWallsConfig({ orientation, usePresets: false });
    trackWallOrientationSelected(calculationMode, orientation, wallsStepNumber);
  };

  const handleSlatWallStartHeightChange = (startHeight: number) => {
    updateWallsConfig({ slatWallStartHeight: startHeight, slatWallCoveragePercent: 0, usePresets: false });
  };

  const handleSlatWallNumPanelsChange = (numPanels: number) => {
    updateWallsConfig({ slatWallNumPanels: numPanels, slatWallCoveragePercent: 0, usePresets: false });
  };

  const handleSlatWallColorChange = (color: ProductColor) => {
    updateWallsConfig({ slatWallColor: color, usePresets: false });
  };

  // On blur of wall dimension inputs, auto-adjust garage door if it no longer fits
  const handleDimensionBlur = (index: number) => {
    const wall = individualWalls?.[index];
    if (!wall?.garage?.enabled) return;

    const currentDoorFits = wall.garage.width <= wall.length && wall.garage.height <= wall.height;
    if (!currentDoorFits && wall.length >= 4 && wall.height >= 6) {
      const best = findBestFitDoor(wall.length, wall.height);
      const maxDoors = Math.max(1, Math.floor(wall.length / best.width));
      updateIndividualWall(index, {
        garage: {
          ...wall.garage,
          width: best.width,
          height: best.height,
          numDoors: Math.min(wall.garage.numDoors, maxDoors),
        },
      });
    }
  };

  // Find the best-fit garage door preset for given wall dimensions
  const findBestFitDoor = (wallLength: number, wallHeight: number) => {
    // Filter presets that fit, then pick the smallest one
    const fitting = GARAGE_DOOR_PRESETS.filter(p => p.width <= wallLength && p.height <= wallHeight);
    if (fitting.length > 0) {
      return { width: fitting[0].width, height: fitting[0].height };
    }
    // Nothing fits — use minimum valid custom size
    return { width: Math.min(4, wallLength), height: Math.min(6, wallHeight) };
  };

  // GTM: debounced, gate-until-complete tracking for `estimator_wall_configured`.
  // Per marketing spec we only want the event once a wall has every required
  // field filled — not on every keystroke while the user is typing dimensions.
  // Timers are keyed by wall index so edits to different walls don't clobber
  // each other.
  const wallTrackTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  useEffect(() => {
    const timers = wallTrackTimersRef.current;
    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const scheduleWallConfiguredEvent = (index: number, wall: WallConfig, totalWalls: number) => {
    const timers = wallTrackTimersRef.current;
    const existing = timers.get(index);
    if (existing) clearTimeout(existing);

    // Required-for-"complete" fields. Color + orientation always carry a
    // default; length + height are the actual gate.
    const complete = wall.length > 0 && wall.height > 0;
    if (!complete) {
      timers.delete(index);
      return;
    }

    const timer = setTimeout(() => {
      trackWallConfigured(
        index,
        wall.name,
        String(wall.color),
        String(wall.orientation),
        wall.length,
        wall.height,
        wall.slatWall?.numPanelRows > 0,
        wall.garage?.enabled ?? false,
        totalWalls,
      );
      timers.delete(index);
    }, 500);

    timers.set(index, timer);
  };

  // Individual wall handlers
  const handleWallChange = (index: number, field: keyof WallConfig, value: any) => {
    const wall = individualWalls?.[index];
    const updates: any = { [field]: value };

    // Garage door auto-adjust is handled onBlur, not on every keystroke

    updateIndividualWall(index, updates);
    if (['color', 'orientation', 'length', 'height'].includes(field)) {
      if (wall) {
        const u = { ...wall, ...updates } as WallConfig;
        scheduleWallConfiguredEvent(index, u, individualWalls?.length ?? 0);
      }
    }
  };

  const handleWallSlatWallChange = (index: number, field: keyof SlatWallConfig, value: any) => {
    const currentWall = individualWalls?.[index];
    if (!currentWall) return;
    updateIndividualWall(index, {
      slatWall: { ...currentWall.slatWall, [field]: value },
    });
  };

  const handleWallGarageChange = (index: number, field: keyof GarageConfig, value: any) => {
    const currentWall = individualWalls?.[index];
    if (!currentWall) return;

    // When enabling garage, ensure numDoors is at least 1
    if (field === 'enabled' && value === true && (!currentWall.garage.numDoors || currentWall.garage.numDoors < 1)) {
      updateIndividualWall(index, {
        garage: { ...currentWall.garage, enabled: true, numDoors: 1 },
      });
      return;
    }

    updateIndividualWall(index, {
      garage: { ...currentWall.garage, [field]: value },
    });
  };

  // Render Individual Walls mode with full config
  if (calculationMode === 'IndividualWalls') {
    const walls = individualWalls || [];

    return (
      <div className="step-content">
        <div className="step-header">
          <h2 className="step-title">{t('walls.individualTitle')}</h2>
          <p className="step-description">
            {t('walls.individualDescription')}
          </p>
        </div>

        <div className="walls-editor">
          {walls.map((wall, index) => {
            // Calculate max SlatWall panels for this wall
            const maxSlatWallPanels = Math.floor((wall.height - wall.slatWall.startHeight) / 0.5);
            const slatWallHeight = wall.slatWall.numPanelRows * 0.5;

            return (
              <div key={wall.wallId} className="wall-card">
                <div className="wall-card-header">
                  <h3 className="wall-card-title">
                    <input
                      type="text"
                      value={wall.name}
                      onChange={(e) => handleWallChange(index, 'name', e.target.value)}
                      className="field-input"
                      style={{ width: '150px', fontWeight: 600 }}
                      data-field-id={`wall-${index}-name`}
                    />
                  </h3>
                  <div className="wall-card-actions">
                    <button
                      type="button"
                      className="wall-card-copy"
                      onClick={() => {
                        if (!wall.name?.trim()) {
                          showToast(t('walls.enterWallName'), `wall-${index}-name`);
                          return;
                        }
                        copyIndividualWall(index);
                      }}
                      title={!wall.name?.trim() ? t('walls.fixWallName') : t('walls.copyWall')}
                    >
                      <Copy size={16} />
                      {t('shared.copy')}
                    </button>
                    {walls.length > 1 && (
                      <button
                        type="button"
                        className="wall-card-remove"
                        onClick={() => removeIndividualWall(index)}
                      >
                        {t('shared.remove')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Basic Wall Settings */}
                <div className="wall-card-fields-grid">
                  {/* Row 1: Length and Height - Centered */}
                  <WallDimensionFields
                    unitKey={`wall-${index}`}
                    length={wall.length}
                    height={wall.height}
                    onChange={(field, v) => {
                      handleWallChange(index, field, v);
                      handleDimensionBlur(index);
                    }}
                  />

                  {/* Row 2: Color and Orientation - Bootstrap row layout */}
                  <div className="row g-4 justify-content-center">
                    <div className="col-12 col-lg-auto">
                      <div className="form-section">
                        <h3 className="section-title">{t('shared.wallPanelColor')}</h3>
                        <div className="color-selector with-images">
                          <button
                            type="button"
                            className={`icon-select-btn ${wall.color === 'White' ? 'selected' : ''}`}
                            onClick={() => handleWallChange(index, 'color', 'White')}
                          >
                            <div className="icon-select-img">
                              <img
                                src={WALL_PANEL_IMAGES.White.thumbnail}
                                alt={t('common:productAlt.wcb', { color: t('common:colors.White') })}
                                className="product-image"
                              />
                            </div>
                            <span className="icon-select-label">{t('common:colors.White')}</span>
                          </button>
                          <button
                            type="button"
                            className={`icon-select-btn ${wall.color === 'Gray' ? 'selected' : ''}`}
                            onClick={() => handleWallChange(index, 'color', 'Gray')}
                          >
                            <div className="icon-select-img">
                              <img
                                src={WALL_PANEL_IMAGES.Gray.thumbnail}
                                alt={t('common:productAlt.wcb', { color: t('common:colors.Gray') })}
                                className="product-image"
                              />
                            </div>
                            <span className="icon-select-label">{t('common:colors.Gray')}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="col-auto d-none d-lg-flex align-items-center px-0">
                      <div style={{ width: '1px', height: '75%', backgroundColor: '#dee2e6' }}></div>
                    </div>
                    <div className="col-12 col-lg-auto">
                      <div className="form-section">
                        <h3 className="section-title">{t('shared.panelOrientation')}</h3>
                        <div className="orientation-image-selector">
                          <button
                            type="button"
                            className={`icon-select-btn ${wall.orientation === 'Vertical' ? 'selected' : ''}`}
                            onClick={() => handleWallChange(index, 'orientation', 'Vertical')}
                          >
                            <div className="icon-select-img">
                              <img
                                src={LEGACY_UI_ICONS.wcbVertical}
                                alt={t('common:orientations.Vertical')}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            </div>
                            <span className="icon-select-label">{t('common:orientations.Vertical')}</span>
                          </button>
                          <button
                            type="button"
                            className={`icon-select-btn ${wall.orientation === 'Horizontal' ? 'selected' : ''}`}
                            onClick={() => handleWallChange(index, 'orientation', 'Horizontal')}
                          >
                            <div className="icon-select-img">
                              <img
                                src={LEGACY_UI_ICONS.wcbHorizontal}
                                alt={t('common:orientations.Horizontal')}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            </div>
                            <span className="icon-select-label">{t('common:orientations.Horizontal')}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SlatWall Section */}
                <div className="wall-section-divider text-center">
                  <h3 className="section-title justify-content-center">{t('walls.slatWall.sectionIndividual')}</h3>
                  <div className="slatwall-config-section">
                    <div className="toggle-row justify-content-center mb-2">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={wall.slatWall.enabled}
                          onChange={(e) => handleWallSlatWallChange(index, 'enabled', e.target.checked)}
                        />
                        <span className="toggle-slider" />
                      </label>
                      <span className="toggle-label">
                        {wall.slatWall.enabled ? t('walls.slatWall.enabledWall') : t('walls.slatWall.disabled')}
                      </span>
                    </div>

                    {wall.slatWall.enabled && (
                      <div className="slatwall-fields">
                        <SlatWallFields
                          unitKey={`wall-${index}-slatwall`}
                          startHeight={wall.slatWall.startHeight}
                          numPanels={wall.slatWall.numPanelRows}
                          startMax={wall.height - 0.5}
                          maxPanels={maxSlatWallPanels}
                          onStartHeightChange={(v) => handleWallSlatWallChange(index, 'startHeight', v)}
                          onPanelsChange={(v) => handleWallSlatWallChange(index, 'numPanelRows', v)}
                          panelsFieldId={`wall-${index}-slatwall-panels`}
                        />

                        <div className="field-group">
                          <label className="field-label">{t('shared.color')}</label>
                          <div className="color-selector with-images">
                            <button
                              type="button"
                              className={`icon-select-btn ${wall.slatWall.color === 'White' ? 'selected' : ''}`}
                              onClick={() => handleWallSlatWallChange(index, 'color', 'White')}
                            >
                              <div className="icon-select-img">
                                <img
                                  src={SLATWALL_IMAGES.White.thumbnail}
                                  alt={t('common:productAlt.slatWall', { color: t('common:colors.White') })}
                                  className="product-image"
                                />
                              </div>
                              <span className="icon-select-label">{t('common:colors.White')}</span>
                            </button>
                            <button
                              type="button"
                              className={`icon-select-btn ${wall.slatWall.color === 'Gray' ? 'selected' : ''}`}
                              onClick={() => handleWallSlatWallChange(index, 'color', 'Gray')}
                            >
                              <div className="icon-select-img">
                                <img
                                  src={SLATWALL_IMAGES.Gray.thumbnail}
                                  alt={t('common:productAlt.slatWall', { color: t('common:colors.Gray') })}
                                  className="product-image"
                                />
                              </div>
                              <span className="icon-select-label">{t('common:colors.Gray')}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {wall.slatWall.enabled && slatWallHeight > 0 && (
                      <p className="info-text" style={{ marginTop: '8px' }}>
                        {t('walls.slatWall.heightInfo', { feet: slatWallHeight.toFixed(1), count: wall.slatWall.numPanelRows })}
                      </p>
                    )}
                  </div>
                </div>

                {/* Garage Door Section */}
                <div className="wall-section-divider text-center">
                  <h3 className="section-title justify-content-center">{t('walls.garage.section')}</h3>
                  <div className="garage-config-section">
                    <div className="toggle-row justify-content-center mb-2">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={wall.garage.enabled}
                          onChange={(e) => handleWallGarageChange(index, 'enabled', e.target.checked)}
                        />
                        <span className="toggle-slider" />
                      </label>
                      <span className="toggle-label">
                        {wall.garage.enabled ? t('walls.garage.enabled') : t('walls.garage.disabled')}
                      </span>
                    </div>

                    {wall.garage.enabled && (
                      <div className="garage-door-config">
                        {/* Door Size Presets */}
                        <div className="form-group text-center mb-2">
                          <label className="form-label">{t('shared.doorPresetsLabel')}</label>
                          <div className="door-presets-grid justify-content-center">
                            {GARAGE_DOOR_PRESETS.map(preset => {
                              const doesNotFit = preset.width > wall.length || preset.height > wall.height;
                              const isSelected = wall.garage.width === preset.width &&
                                wall.garage.height === preset.height;
                              return (
                                <button
                                  key={preset.label}
                                  type="button"
                                  className={`door-preset-btn ${isSelected ? 'selected' : ''} ${doesNotFit ? 'disabled' : ''}`}
                                  onClick={() => {
                                    if (doesNotFit) {
                                      showToast(t('application.garageDoors.doesNotFit'));
                                      return;
                                    }
                                                                        updateIndividualWall(index, {
                                      garage: {
                                        ...wall.garage,
                                        width: preset.width,
                                        height: preset.height,
                                      },
                                    });
                                  }}
                                  title={doesNotFit
                                    ? t('walls.garage.doesNotFitTitle', {
                                        doorWidth: preset.width,
                                        doorHeight: preset.height,
                                        wallLength: wall.length,
                                        wallHeight: wall.height,
                                      })
                                    : t(`application.garageDoors.preset.${preset.sizeClass}`, { width: preset.width, height: preset.height })}
                                >
                                  {t(`application.garageDoors.preset.${preset.sizeClass}`, { width: preset.width, height: preset.height })}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Custom Door Size + Number of Doors */}
                        <div className="row g-3 justify-content-center">
                          <div className="col-12 col-md-6">
                            <div className="card h-100 border-light">
                              <div className="card-body text-center">
                                <label className="form-label fw-semibold">{t('shared.customSize')}</label>
                                <GarageDimensionFields
                                  unitKey={`wall-${index}-garage`}
                                  width={wall.garage.width}
                                  height={wall.garage.height}
                                  wallLength={wall.length}
                                  wallHeight={wall.height}
                                  onChange={(field, v) => handleWallGarageChange(index, field, v)}
                                  widthFieldId={`wall-${index}-garage-width`}
                                  heightFieldId={`wall-${index}-garage-height`}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="card h-100 border-light">
                              <div className="card-body text-center">
                                <label className="form-label fw-semibold">{t('shared.numDoors')}</label>
                                <div className="num-doors-selector justify-content-center">
                                  {[1, 2, 3, 4].map(num => {
                                    const invalidSize = wall.garage.width < 4 || wall.garage.width > wall.length || wall.garage.height < 6 || wall.garage.height > wall.height;
                                    const wouldExceed = wall.garage.width * num > wall.length || invalidSize;
                                    return (
                                      <button
                                        key={num}
                                        type="button"
                                        className={`num-door-btn ${wall.garage.numDoors === num && !wouldExceed ? 'selected' : ''} ${wouldExceed ? 'disabled' : ''}`}
                                        onClick={() => {
                                          if (wouldExceed) {
                                            showToast(t('application.garageDoors.doesNotFit'));
                                          } else {
                                            handleWallGarageChange(index, 'numDoors', num);
                                          }
                                        }}
                                        title={wouldExceed
                                          ? (invalidSize
                                              ? t('application.garageDoors.fixSizeFirst')
                                              : t('application.garageDoors.numDoorsExceeds', { count: num, total: wall.garage.width * num, wallLength: wall.length }))
                                          : t('application.garageDoors.doorCount', { count: num })}
                                      >
                                        {num}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {wall.garage.enabled && wall.garage.numDoors * wall.garage.width > wall.length && (
                      <div className="validation-warning">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {t('walls.garage.totalWidthWarning')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Wall 3D Preview */}
                <Wall3DViewer
                  wallLength={wall.length}
                  wallHeight={wall.height}
                  wallColor={wall.color}
                  orientation={wall.orientation}
                  wallName={wall.name || t('walls.fallbackName', { number: index + 1 })}
                  slatWallConfig={wall.slatWall.enabled && wall.slatWall.numPanelRows > 0 ? {
                    enabled: true,
                    startHeight: wall.slatWall.startHeight,
                    numPanelRows: wall.slatWall.numPanelRows,
                    color: wall.slatWall.color,
                  } : null}
                  garageConfig={wall.garage?.enabled ? {
                    enabled: true,
                    width: wall.garage.width,
                    height: wall.garage.height,
                    numDoors: wall.garage.numDoors,
                  } : null}
                />
              </div>
            );
          })}

          <button type="button" className="add-wall-button" onClick={() => { addIndividualWall(); trackWallAdded((individualWalls?.length || 0) + 1); }}>
            <Plus size={20} />
            {t('walls.addWall')}
          </button>
        </div>

        {/* Summary */}
        <div className="alert alert-tc-info d-flex align-items-start gap-3 mt-4" role="alert">
          <div className="flex-shrink-0 text-info">
            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <strong>{t('walls.wallsConfigured', { count: walls.length })}</strong>
            <p className="mb-0 small mt-1">
              {t('walls.totalArea', { value: walls.reduce((sum, w) => sum + w.length * w.height, 0).toLocaleString() })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Get current wall height from dimensions config
  const currentWallHeight = dimensionsConfig.customDimensions?.height
    || ROOM_PRESETS.find(p => p.id === dimensionsConfig.presetId)?.height
    || 10;

  // Calculate dynamic max for slatwall fields
  const maxSlatWallStartHeight = Math.max(0, currentWallHeight - 0.5);
  const maxSlatWallPanels = Math.max(0, Math.floor((currentWallHeight - wallsConfig.slatWallStartHeight) / 0.5));

  // Calculate SlatWall height for display (Full Room mode)
  const slatWallHeight = wallsConfig.slatWallNumPanels * 0.5;

  // Render Full Room mode (default)
  return (
    <div className="step-content">
      <div className="step-header">
        <h2 className="step-title">{t('walls.title')}</h2>
        <p className="step-description">
          {t('walls.description')}
        </p>
      </div>

      {/* Preset vs Custom Toggle */}
      <div className="mode-toggle">
        <button
          type="button"
          className={`mode-button ${usePresets ? 'active' : ''}`}
          onClick={() => setUsePresets(true)}
        >
          <Sparkles size={16} />
          {t('walls.presets.designPresets')}
        </button>
        <button
          type="button"
          className={`mode-button ${!usePresets ? 'active' : ''}`}
          onClick={() => {
            setUsePresets(false);
            setSelectedPresetId(null);
          }}
        >
          <Settings size={16} />
          {t('walls.presets.customConfiguration')}
        </button>
      </div>

      {/* Design Presets Section */}
      {usePresets && (
        <div className="form-section">
          <h3 className="section-title">
            {t('walls.presets.quickStart')}
            <Tooltip content={t('walls.presets.quickStartTooltip')} />
          </h3>
          <div className="row g-3 justify-content-center" data-field-id="design-presets">
            {DESIGN_PRESETS.map(preset => {
              const presetImage = LEGACY_DESIGN_PRESET_IMAGES[preset.id as keyof typeof LEGACY_DESIGN_PRESET_IMAGES];
              return (
                <div key={preset.id} className="col-12 col-sm-6 col-lg-4 col-xl">
                  <button
                    type="button"
                    className={`design-preset-card ${selectedPresetId === preset.id ? 'selected' : ''}`}
                    onClick={() => handleApplyDesignPreset(preset.id)}
                    onDoubleClick={() => { handleApplyDesignPreset(preset.id); nextStep(); }}
                  >
                    <div className="design-preset-body">
                      {presetImage && (
                        <div className="design-preset-image">
                          <img
                            src={presetImage}
                            alt={t(`walls.presets.${preset.id}.name`)}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <span className="design-preset-name">{t(`walls.presets.${preset.id}.name`)}</span>
                      <span className="design-preset-description">{t(`walls.presets.${preset.id}.description`)}</span>
                    </div>
                    <div className="design-preset-footer">
                      <span className={`badge ${preset.wallColor === 'Gray' ? 'bg-secondary border border-secondary' : 'bg-light text-dark border'}`}>
                        {t('walls.presets.wallsBadge', { color: t(`common:colors.${preset.wallColor}`) })}
                      </span>
                      {preset.slatWallCoverage > 0 && (
                        <span className={`badge ${preset.slatWallColor === 'Gray' ? 'bg-secondary border border-secondary' : 'bg-light text-dark border'}`}>
                          {t('walls.presets.slatWallBadge', { percent: preset.slatWallCoverage })}
                        </span>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Configuration Summary for Presets */}
      {usePresets && selectedPresetId && (
        <div className="form-section">
          <h3 className="section-title">{t('walls.presets.selected')}</h3>
          <div className="preset-summary">
            {(() => {
              const preset = DESIGN_PRESETS.find(p => p.id === selectedPresetId);
              if (!preset) return null;
              return (
                <div className="preset-summary-content">
                  <div className="preset-summary-item">
                    <span className="preset-summary-label">{t('walls.presets.wallPanels')}</span>
                    <span className="preset-summary-value">{t('walls.presets.wallPanelsValue', { color: t(`common:colors.${preset.wallColor}`), orientation: t(`common:orientations.${preset.wallOrientation}`) })}</span>
                  </div>
                  <div className="preset-summary-item">
                    <span className="preset-summary-label">{t('walls.presets.slatWall')}</span>
                    <span className="preset-summary-value">
                      {preset.slatWallCoverage > 0
                        ? t('walls.presets.slatWallValue', { percent: preset.slatWallCoverage, color: t(`common:colors.${preset.slatWallColor}`) })
                        : t('walls.presets.slatWallNone')}
                    </span>
                  </div>
                  <div className="preset-summary-item">
                    <span className="preset-summary-label">{t('walls.presets.ceiling')}</span>
                    <span className="preset-summary-value">
                      {preset.includeCeiling ? t(`common:colors.${preset.ceilingColor}`) : t('walls.presets.ceilingNotIncluded')}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Custom Configuration - Only show when NOT using presets */}
      {!usePresets && (
        <>
          <div className="row g-4 justify-content-center">
          {/* Wall Color Selection */}
          <div className="col-12 col-lg-auto">
          <div className="form-section">
            <h3 className="section-title">{t('shared.wallPanelColor')}</h3>
            <div className="color-selector with-images">
              <button
                type="button"
                className={`icon-select-btn ${wallsConfig.color === 'White' ? 'selected' : ''}`}
                onClick={() => handleColorChange('White')}
              >
                <div className="icon-select-img">
                  <img
                    src={WALL_PANEL_IMAGES.White.thumbnail}
                    alt={t('common:productAlt.wcb', { color: t('common:colors.White') })}
                    className="product-image"
                    onError={(e) => {
                      // Fallback to color swatch if image fails
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                  <div className="color-sample white hidden" />
                </div>
                <span className="icon-select-label">{t('common:colors.White')}</span>
              </button>
              <button
                type="button"
                className={`icon-select-btn ${wallsConfig.color === 'Gray' ? 'selected' : ''}`}
                onClick={() => handleColorChange('Gray')}
              >
                <div className="icon-select-img">
                  <img
                    src={WALL_PANEL_IMAGES.Gray.thumbnail}
                    alt={t('common:productAlt.wcb', { color: t('common:colors.Gray') })}
                    className="product-image"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                  <div className="color-sample gray hidden" />
                </div>
                <span className="icon-select-label">{t('common:colors.Gray')}</span>
              </button>
            </div>
          </div>
          </div>

          {/* Vertical divider — visible only when side-by-side */}
          <div className="col-auto d-none d-lg-flex align-items-center px-0">
            <div style={{ width: '1px', height: '75%', backgroundColor: '#dee2e6' }}></div>
          </div>

          {/* Panel Orientation */}
          <div className="col-12 col-lg-auto">
          <div className="form-section">
            <h3 className="section-title">
              {t('shared.panelOrientation')}
              <Tooltip content={t('walls.presets.orientationTooltip')} />
            </h3>
            <div className="orientation-image-selector">
              <button
                type="button"
                className={`icon-select-btn ${wallsConfig.orientation === 'Vertical' ? 'selected' : ''}`}
                onClick={() => handleOrientationChange('Vertical')}
              >
                <div className="icon-select-img">
                  <img
                    src={LEGACY_UI_ICONS.wcbVertical}
                    alt={t('common:orientations.Vertical')}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                <span className="icon-select-label">{t('common:orientations.Vertical')}</span>
              </button>

              <button
                type="button"
                className={`icon-select-btn ${wallsConfig.orientation === 'Horizontal' ? 'selected' : ''}`}
                onClick={() => handleOrientationChange('Horizontal')}
              >
                <div className="icon-select-img">
                  <img
                    src={LEGACY_UI_ICONS.wcbHorizontal}
                    alt={t('common:orientations.Horizontal')}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                <span className="icon-select-label">{t('common:orientations.Horizontal')}</span>
              </button>
            </div>
          </div>
          </div>
          </div>

          {/* SlatWall Configuration - Optional Add-On */}
          <div className="form-section">
            <h3 className="section-title">
              {t('walls.slatWall.section')}
              <Tooltip content={t('walls.slatWall.tooltip')} />
            </h3>
            <div className="toggle-group">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={slatWallEnabled}
                  onChange={(e) => {
                    setSlatWallEnabled(e.target.checked);
                    trackSlatwallToggled(calculationMode, e.target.checked, wallsStepNumber);
                    if (!e.target.checked) {
                      // Disable - reset to 0
                      handleSlatWallNumPanelsChange(0);
                      handleSlatWallStartHeightChange(0);
                    }
                  }}
                />
                <span className="toggle-slider" />
              </label>
              <span className="toggle-label">
                {slatWallEnabled
                  ? t('walls.slatWall.enabled')
                  : t('walls.slatWall.disabled')}
              </span>
            </div>

            {slatWallEnabled && (
              <div className="slatwall-config-section mt-3">
                {state.completedSteps.includes('dimensions') && (
                  <p className="text-secondary text-center mb-3">
                    {t('walls.slatWall.wallHeight')} <strong>{currentWallHeight}'</strong>
                  </p>
                )}
                <div className="slatwall-fields">
                  <SlatWallFields
                    unitKey="walls-slatwall"
                    startHeight={wallsConfig.slatWallStartHeight}
                    numPanels={wallsConfig.slatWallNumPanels}
                    startMax={maxSlatWallStartHeight}
                    maxPanels={maxSlatWallPanels}
                    onStartHeightChange={handleSlatWallStartHeightChange}
                    onPanelsChange={handleSlatWallNumPanelsChange}
                    panelsFieldId="slatwall-panels"
                  />

                  <div className="field-group">
                    <label className="field-label">{t('walls.slatWall.color')}</label>
                    <div className="color-selector with-images">
                      <button
                        type="button"
                        className={`icon-select-btn ${wallsConfig.slatWallColor === 'White' ? 'selected' : ''}`}
                        onClick={() => handleSlatWallColorChange('White')}
                      >
                        <div className="icon-select-img">
                          <img
                            src={SLATWALL_IMAGES.White.thumbnail}
                            alt={t('common:productAlt.slatWall', { color: t('common:colors.White') })}
                            className="color-image"
                          />
                        </div>
                        <span className="icon-select-label">{t('common:colors.White')}</span>
                      </button>
                      <button
                        type="button"
                        className={`icon-select-btn ${wallsConfig.slatWallColor === 'Gray' ? 'selected' : ''}`}
                        onClick={() => handleSlatWallColorChange('Gray')}
                      >
                        <div className="icon-select-img">
                          <img
                            src={SLATWALL_IMAGES.Gray.thumbnail}
                            alt={t('common:productAlt.slatWall', { color: t('common:colors.Gray') })}
                            className="color-image"
                          />
                        </div>
                        <span className="icon-select-label">{t('common:colors.Gray')}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="calculated-area" style={{ marginTop: '16px' }}>
                  <p>
                    {t('walls.slatWall.heightSummary')} <strong>{t('walls.slatWall.feet', { value: slatWallHeight.toFixed(1) })}</strong>
                  </p>
                  <p>
                    {t('walls.slatWall.rangeSummary')} <strong>{t('walls.slatWall.rangeValue', { from: wallsConfig.slatWallStartHeight, to: (wallsConfig.slatWallStartHeight + slatWallHeight).toFixed(1) })}</strong> {t('walls.slatWall.fromFloor')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default WallsStep;
